import { useEffect } from 'react';
import { Outlet } from 'react-router';
import { Button } from '@/components/ui/Button';
import { useAuth } from './use-auth';
import { useAuthDialog } from './use-auth-dialog';

/**
 * Asks anonymous visitors to sign in, WITHOUT moving them.
 *
 * It used to redirect to /login with a `next` parameter. Opening the dialog in
 * place is better on every count: the address stays on the page they wanted, so
 * signing in simply reveals it — no round trip, no redirect target to get wrong,
 * and the back button still does what they expect.
 *
 * A usability control, not a security one — the API enforces authorization on
 * every request regardless of what the bundle does.
 */
export function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  const { open } = useAuthDialog();

  const shouldPrompt = !isLoading && !isAuthenticated;

  useEffect(() => {
    if (shouldPrompt) {
      open('login');
    }
  }, [shouldPrompt, open]);

  if (isLoading) {
    // The session probe is still running. Prompting here would ask a signed-in
    // user to sign in again on every reload.
    return (
      <p className="py-20 text-center text-surface-500" role="status" aria-live="polite">
        Yükleniyor…
      </p>
    );
  }

  if (!isAuthenticated) {
    // Rendered behind the dialog, and still there if it is dismissed — otherwise
    // closing it would leave a blank page with no way back in.
    return (
      <div className="status-page text-center">
        <h1 className="text-xl font-semibold tracking-tight text-surface-50">
          Bu sayfa için giriş yapmalısın
        </h1>
        <p className="mt-2 text-sm text-surface-400">
          Hesabına girdiğinde kaldığın yerden devam edeceksin.
        </p>

        <Button type="button" className="mt-6" onClick={() => open('login')}>
          Giriş yap
        </Button>
      </div>
    );
  }

  return <Outlet />;
}
